import React, { useEffect, useRef, useState } from "react";
import { Nav } from "react-bootstrap";
import {
  FaAddressBook,
  FaBook,
  FaChevronDown,
  FaChevronUp,
  FaFileAlt,
  FaFilePdf,
  FaFolderOpen,
  FaHandshake,
  FaPhone,
  FaSignature,
  FaTachometerAlt,
  FaUser,
} from "react-icons/fa";
import { MdInsertInvitation } from "react-icons/md";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./Sidebar.css";

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem("user"));
  const userName = user?.userName;
  const [activeLink, setActiveLink] = useState(location.pathname);
  const [isMyDocsOpen, setIsMyDocsOpen] = useState(false);
  const scrollContainerRef = useRef(null);
  const activeSidebar = location.state?.activeSidebar;
  useEffect(() => {
    if (scrollContainerRef.current) {
      const savedScrollPosition = sessionStorage.getItem(
        "leftSideScrollPosition",
      );
      if (savedScrollPosition) {
        scrollContainerRef.current.scrollTop = parseInt(
          savedScrollPosition,
          10,
        );
      }
    }

    const handleScroll = () => {
      if (scrollContainerRef.current) {
        const scrollPosition = scrollContainerRef.current.scrollTop;
        sessionStorage.setItem(
          "leftSideScrollPosition",
          scrollPosition.toString(),
        );
      }
    };

    const currentRef = scrollContainerRef.current;
    if (currentRef) {
      currentRef.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener("scroll", handleScroll);
      }
    };
  }, [location]);

  // Auto-expand My Docs group when on a related route
  useEffect(() => {
    if (
      location.pathname.startsWith("/dashboard/my-docs") ||
      location.pathname.startsWith("/dashboard/review-documents") ||
      location.pathname.startsWith("/hr-dashboard/evergreenjobs-applications")
    ) {
      setIsMyDocsOpen(true);
    }
  }, [location]);

  const navLinks = [
    {
      to: "/dashboard",
      label: "Dashboard",
      icon: <FaTachometerAlt size={30} />,
    },
    {
      to: "/dashboard/my-consents",
      label: "Consents & Agreements",
      icon: <FaFileAlt size={30} />,
    },
    {
      to: "/dashboard/my-collabs",
      label: "Collabs",
      icon: <FaHandshake size={30} />,
    },
    {
      group: "my-docs",
      label: "My Docs",
      icon: <FaFolderOpen size={30} />,
      children: [
        {
          to: "/dashboard/my-docs",
          label: "For Sign",
          icon: <FaSignature size={18} />,
        },
        {
          to: "/dashboard/review-documents?tab=unReviewedConsents",
          label: "For Review",
          icon: <FaFileAlt size={18} />,
        },
      ],
    },
    {
      to: "/dashboard/templates?tab=consent",
      label: "Templates",
      icon: <FaFilePdf size={30} />,
    },
    {
      to: "/dashboard/contacts",
      label: "Contacts",
      icon: <FaAddressBook size={30} />,
    },
    {
      to: "/dashboard/creditPassBook?tab=user",
      label: "Frymes PassBook",
      icon: <FaBook size={30} />,
    },
    {
      to: "/dashboard/invitations?tab=sent-invitations",
      label: "Invitations",
      icon: <MdInsertInvitation size={30} />,
    },
    { to: "/dashboard/profile", label: "Profile", icon: <FaUser size={30} /> },
    {
      to: "/dashboard/schedule-meeting",
      label: "Talk to Expert",
      icon: <FaPhone size={30} />,
    },
  ];

  const handleLinkClick = (to) => {
    setActiveLink(to);

    if (location.pathname === to) {
      navigate(to, { state: { userName, refresh: Date.now() } });
    } else {
      navigate(to, { state: { userName } });
    }
  };

  const isLinkActive = (link) => {
    const currentPath = location.pathname;
    const linkPathname = link.to.split("?")[0];

    // =====================================================
    // IF A PAGE SENT activeSidebar, USE IT
    // =====================================================
    if (activeSidebar) {
      return linkPathname === activeSidebar;
    }

    // =====================================================
    // MY CONSENTS
    // =====================================================
    if (linkPathname === "/dashboard/my-consents") {
      return (
        currentPath === "/dashboard/my-consents" ||
        currentPath.startsWith("/dashboard/my-consents/")
      );
    }

    // =====================================================
    // MY DOCS
    // =====================================================
    if (linkPathname === "/dashboard/my-docs") {
      return (
        currentPath === "/dashboard/my-docs" ||
        currentPath.startsWith("/dashboard/my-docs/")
      );
    }

    // =====================================================
    // REVIEW DOCUMENTS
    // =====================================================
    if (linkPathname === "/dashboard/review-documents") {
      return (
        currentPath === "/dashboard/review-documents" ||
        currentPath.startsWith("/dashboard/review-documents/")
      );
    }

    // =====================================================
    // MY COLLABS
    // =====================================================
    if (linkPathname === "/dashboard/my-collabs") {
      return (
        currentPath === "/dashboard/my-collabs" ||
        currentPath.startsWith("/dashboard/my-collabs/")
      );
    }

    // =====================================================
    // INVITATIONS
    // =====================================================
    if (linkPathname === "/dashboard/invitations") {
      return (
        currentPath === "/dashboard/invitations" ||
        currentPath.startsWith("/dashboard/invitations/")
      );
    }

    // =====================================================
    // CREDIT PASSBOOK
    // =====================================================
    if (linkPathname === "/dashboard/creditPassBook") {
      return (
        currentPath === "/dashboard/creditPassBook" ||
        currentPath.startsWith("/dashboard/creditPassBook/")
      );
    }

    // =====================================================
    // CONTACTS
    // =====================================================
    if (linkPathname === "/dashboard/contacts") {
      return (
        currentPath === "/dashboard/contacts" ||
        currentPath.startsWith("/dashboard/private-chat")
      );
    }

    // =====================================================
    // PROFILE
    // =====================================================
    if (linkPathname === "/dashboard/profile") {
      return (
        currentPath === "/dashboard/profile" ||
        currentPath.startsWith("/dashboard/profile/")
      );
    }

    // =====================================================
    // TEMPLATES
    // =====================================================
    if (linkPathname === "/dashboard/templates") {
      return (
        currentPath === "/dashboard/templates" ||
        currentPath.startsWith("/dashboard/templates/")
      );
    }

    // =====================================================
    // SCHEDULE MEETING
    // =====================================================
    if (linkPathname === "/dashboard/schedule-meeting") {
      return (
        currentPath === "/dashboard/schedule-meeting" ||
        currentPath.startsWith("/dashboard/schedule-meeting/")
      );
    }

    // =====================================================
    // CREDIT REQUEST
    // =====================================================
    if (linkPathname === "/dashboard/creditRequest") {
      return (
        currentPath === "/dashboard/creditRequest" ||
        currentPath.startsWith("/dashboard/creditRequest/")
      );
    }

    // =====================================================
    // DEFAULT
    // =====================================================
    return currentPath === linkPathname;
  };

  // True if any child of the My Docs group is active (used to highlight the parent)
  const isGroupActive = (group) => {
    const currentPath = location.pathname;
    if (group.group === "my-docs") {
      return (
        currentPath.startsWith("/dashboard/my-docs") ||
        currentPath.startsWith("/dashboard/review-documents") ||
        currentPath.startsWith("/hr-dashboard/evergreenjobs-applications")
      );
    }
    return false;
  };

  const renderSingleLink = (link) => (
    <div
      style={{
        position: "relative",
        width: "100%",
        marginBottom: "20px",
      }}
    >
      <Link
        to={link.to}
        onClick={(e) => {
          e.preventDefault();
          handleLinkClick(link.to);
        }}
        className={`nav-link d-flex flex-column align-items-center ${
          isLinkActive(link) ? "active" : ""
        }`}
        style={{
          fontSize: "1.1rem",
          color: isLinkActive(link) ? "#663399" : "#332e38",
          textAlign: "center",
          padding: "10px",
          transition: "color 0.3s",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            position: "relative",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
              position: "relative",
              height: "50px",
              width: "auto",
              minWidth: "100px",
              justifyContent: "center",
            }}
          >
            {link.icon && (
              <span style={{ marginBottom: "10px" }}>{link.icon}</span>
            )}
            {link.label}
          </div>

          <div
            className="triangle"
            style={{
              width: "0",
              height: "0",
              borderStyle: "solid",
              borderWidth: "0 0 30px 30px",
              borderColor: "transparent transparent #663399 transparent",
              position: "absolute",
              top: "70%",
              left: "100%",
              marginLeft: "10px",
              display: isLinkActive(link) ? "block" : "none",
            }}
          />
        </div>
      </Link>

      <hr style={{ width: "100%", borderColor: "black", margin: "5px 0" }} />
    </div>
  );

  const renderGroupLink = (group) => {
    const groupActive = isGroupActive(group);

    return (
      <div
        style={{
          position: "relative",
          width: "100%",
          marginBottom: "20px",
        }}
      >
        <div
          onClick={() => setIsMyDocsOpen((prev) => !prev)}
          className={`nav-link d-flex flex-column align-items-center ${
            groupActive ? "active" : ""
          }`}
          style={{
            fontSize: "1.1rem",
            color: groupActive ? "#663399" : "#332e38",
            textAlign: "center",
            padding: "10px",
            transition: "color 0.3s",
            cursor: "pointer",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
              position: "relative",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexDirection: "column",
                position: "relative",
                height: "50px",
                width: "auto",
                minWidth: "100px",
                justifyContent: "center",
              }}
            >
              {group.icon && (
                <span style={{ marginBottom: "10px" }}>{group.icon}</span>
              )}
              <span
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                {group.label}
                {isMyDocsOpen ? (
                  <FaChevronUp size={12} />
                ) : (
                  <FaChevronDown size={12} />
                )}
              </span>
            </div>

            <div
              className="triangle"
              style={{
                width: "0",
                height: "0",
                borderStyle: "solid",
                borderWidth: "0 0 30px 30px",
                borderColor: "transparent transparent #663399 transparent",
                position: "absolute",
                top: "70%",
                left: "100%",
                marginLeft: "10px",
                display: groupActive ? "block" : "none",
              }}
            />
          </div>
        </div>

        {isMyDocsOpen && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
              paddingBottom: "5px",
            }}
          >
            {group.children.map((child, idx) => (
              <Link
                key={idx}
                to={child.to}
                onClick={(e) => {
                  e.preventDefault();
                  handleLinkClick(child.to);
                }}
                className={`nav-link d-flex align-items-center ${
                  isLinkActive(child) ? "active" : ""
                }`}
                style={{
                  fontSize: "0.95rem",
                  color: isLinkActive(child) ? "#663399" : "#332e38",
                  textAlign: "center",
                  padding: "6px 10px",
                  gap: "8px",
                  width: "90%",
                }}
              >
                {child.icon}
                {child.label}
              </Link>
            ))}
          </div>
        )}

        <hr style={{ width: "100%", borderColor: "black", margin: "5px 0" }} />
      </div>
    );
  };

  const renderNavLinks = () => (
    <Nav className="flex-column full-height align-items-center">
      {navLinks.map((link, index) => (
        <React.Fragment key={index}>
          {link.group ? renderGroupLink(link) : renderSingleLink(link)}
        </React.Fragment>
      ))}
    </Nav>
  );

  return (
    <div ref={scrollContainerRef} className="sidebar">
      {renderNavLinks()}
    </div>
  );
};

export default Sidebar;
